import { partition } from 'lodash';
import _isEmpty from 'lodash/isEmpty';
import { format } from 'date-fns';
import {
  clientsCentralSearchService,
  conversationService,
  conversationServiceV2,
  signAttachmentsService,
  profileService,
  attachmentsService,
  attachmentsServiceV2,
  productDetailsService,
  emailTemplatesService,
  farfetchProductDetailsService,
} from 'config/api';
import { MMS_PDF_MB_LIMIT } from 'constants/index';
import { uuidv4 } from 'utils/commonUtils';
import { request } from 'modules/shell/utils/request';
import { convertBytesToMB } from 'modules/clienteling/utils';
import { resetActiveUserADGroup, checkAttachmentsLimit } from 'modules/shell/actions';
import { getSecondsTimestamp } from '../utils';
import actionTypes from './actionTypes';

export const storeProductLink = (payload) => ({
  type: actionTypes.STORE_PRODUCT_LINK,
  payload,
});

export const resetConversation = () => ({
  type: actionTypes.RESET_CONVERSATION,
});

export const addNewRecipient = (recipient) => ({
  type: actionTypes.ADD_NEW_REPCIPIENT,
  payload: recipient,
});

export const addNewRecipients = (recipients) => ({
  type: actionTypes.ADD_NEW_RECIPIENTS,
  payload: recipients,
});
export const replaceRecipients = (recipient) => ({
  type: actionTypes.REPLACE_RECIPIENTS,
  payload: recipient,
});

export const deleteRecipient = (recipient) => ({
  type: actionTypes.DELETE_REPCIPIENT,
  payload: recipient,
});

export const onEnableMessagesCheckbox = (isEditingMessagesEnabled) => ({
  type: actionTypes.TOGGLE_MESSAGES_CHECKBOX,
  payload: isEditingMessagesEnabled,
});

export const getListOfClients =
  (searchTerm = '', page = 1) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.CLIENTS_LIST_LOADING });

    const endpoint = searchTerm
      ? `/customerSearch?q=${searchTerm}&page=${page}&searchLevel=clientList&field=name`
      : `/myCustomers?page=${page}`;

    try {
      const clientCentralResponse = await clientsCentralSearchService.get(endpoint);
      const listOfClients = clientCentralResponse.data?.data;
      const promises = listOfClients.map((p) => profileService.get(`/profiles/${p.profileId}/info`)) || [];
      const cmdProfiles = await Promise.all(promises).then((data) =>
        data.map((i, index) => ({
          profileId: listOfClients[index].profileId,
          ...i.data,
        })),
      );
      const payload = { hasMore: clientCentralResponse.data?.hasMore, data: cmdProfiles };

      dispatch({ type: actionTypes.CLIENTS_LIST_SUCCESS, payload });
    } catch (error) {
      dispatch({
        type: actionTypes.CLIENTS_LIST_ERROR,
        payload: error.response?.data?.message,
      });
    }
  };

export const getMessagesList = (clientId, associateId) => async (dispatch) => {
  dispatch({ type: actionTypes.MESSAGES_LIST_LOADING });

  try {
    const { data } = await conversationService.get(`getInboxDetails/${associateId}/ExternalEntity/${clientId}`);
    const payload = data.Items || [];
    dispatch({ type: actionTypes.MESSAGES_LIST_SUCCESS, payload });
  } catch (error) {
    dispatch({
      type: actionTypes.MESSAGES_LIST_ERROR,
      payload: error.response?.data?.message,
    });
  }
};

export const getMessagesListV2 =
  ({ contactId = '', associateId = '', pagination = {}, isDleChat = false }) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.MESSAGES_LIST_V2_LOADING });

    try {
      const apiData = isDleChat
        ? { associateId, contactId, pagination, messageType: 'dle_chat' }
        : { associateId, contactId, pagination };
      const { data } = await conversationServiceV2.post('/conversation', {
        ...apiData,
      });

      const payload = {
        items: data?.Items.filter((item) => item.MessageType !== 'VOICE'),
        count: data?.Count,
      };
      dispatch({ type: actionTypes.MESSAGES_LIST_V2_SUCCESS, payload });
    } catch (error) {
      dispatch({ type: actionTypes.MESSAGES_LIST_V2_ERROR, payload: error.response?.data?.message });
    }
  };

export const clearConversationMessagesList = () => ({
  type: actionTypes.CLEAR_MESSAGES_LIST,
});

export const addMessage = (message) => ({
  type: actionTypes.ADD_MESSAGE,
  payload: message,
});

export const addTextMessage = (message) => ({
  type: actionTypes.ADD_TEXT_MESSAGE,
  payload: message,
});

export const updateMessageStatus = (message) => ({
  type: actionTypes.UPDATE_MESSAGE,
  payload: message,
});

export const markMessageRead = (message) => ({
  type: actionTypes.MARK_MESSAGE_READ,
  payload: message,
});

export const addImageSourceItems =
  (attachments = []) =>
  async (dispatch) => {
    const isFileAttachments = attachments.filter((item) => _isEmpty(item)).length === attachments.length;
    if (isFileAttachments) {
      const attachmentsAtLimit = await dispatch(checkAttachmentsLimit(attachments, isFileAttachments));
      if (attachmentsAtLimit) return;
    }

    const checkedAttachments = attachments.map((item) => ({ id: uuidv4(), item }));

    dispatch({
      type: actionTypes.GET_ATTACHMENTS_DATASOURCE_SUCCESS,
      payload: checkedAttachments,
    });
  };

export const addAttachments =
  (attachments = [], isEmail) =>
  async (dispatch) => {
    const isFileAttachments = attachments.filter((item) => _isEmpty(item)).length === attachments.length;
    if (isFileAttachments) {
      const attachmentsAtLimit = await dispatch(checkAttachmentsLimit(attachments, isFileAttachments, isEmail));

      if (attachmentsAtLimit) return;
    }

    const checkedAttachments = attachments.map((item) => ({ id: uuidv4(), item }));

    dispatch({
      type: actionTypes.GET_ATTACHMENTS_DATASOURCE_LOADING,
      payload: checkedAttachments,
    });

    // split attachments into two groups: shared products [0] and uploaded photos [1]
    const groupedAttachments = partition(checkedAttachments, ({ item }) => item?.isSharedProduct || item?.isSharedLook);

    try {
      // upload to S3 only photos, not shared products
      const mappedAttributes = groupedAttachments[1].map(({ item: file, id }) => ({
        name: file.name,
        type: file.type,
        id,
      }));
      const { data = [] } = await attachmentsService.post('', mappedAttributes, {
        httpClientOptions: { handleErrors: false },
      });

      dispatch({
        type: actionTypes.GET_ATTACHMENTS_DATASOURCE_SUCCESS,
        payload: [...groupedAttachments[0], ...data],
      });
    } catch (error) {
      dispatch({ type: actionTypes.GET_ATTACHMENTS_DATASOURCE_ERROR, payload: error });
    }
  };
/**
 *
 * @param {{}} cloudinaryConfig
 * @param {[]} attachments
 * @param {boolean} isEmail
 * @param profilePics
 * @returns {(function(*, *): Promise<void>)|*}
 */
export const addCloudinaryAttachments =
  (cloudinaryConfig, attachments = [], isEmail = false) =>
  async (dispatch, getState) => {
    const isFileAttachments = attachments.filter((item) => _isEmpty(item)).length === attachments.length;
    if (isFileAttachments) {
      const attachmentsAtLimit = await dispatch(checkAttachmentsLimit(attachments, isFileAttachments, isEmail));

      if (attachmentsAtLimit) return;
    }

    //* we have to resetAdGroup so it does not get requests header
    resetActiveUserADGroup();

    const { cloudName, apiKey } = cloudinaryConfig;

    const checkedAttachments = attachments.map((item) => ({ id: uuidv4(), item }));
    dispatch({
      type: actionTypes.GET_ATTACHMENTS_DATASOURCE_LOADING,
      payload: checkedAttachments,
    });

    const [uploadedSharedLook, uploadedAttachment] = partition(
      checkedAttachments,
      ({ item }) => item?.isSharedProduct || item?.isSharedLook,
    );

    try {
      /**
       * Skip `signAttachmentsService` when sharing a look
       * the response { data: signatures } is only used when
       * `attachmentsServiceV2` POST is called with the attachment(s) data.
       */
      if (!uploadedAttachment.length) {
        dispatch({
          type: actionTypes.GET_ATTACHMENTS_DATASOURCE_SUCCESS,
          payload: [...uploadedSharedLook],
        });

        return;
      }

      const timestamp = getSecondsTimestamp();
      const formattedDate = format(new Date(), 'yyyy/M/d');
      // UUID for every group uploads
      const id = uuidv4();
      const folder = `OSP/${formattedDate}/${id}`;

      const { imageSource } = getState().clientingModule.conversation;

      const uploadedPublicIds = uploadedAttachment.map((_, idx) => {
        const lastCount = imageSource.length + idx;
        return `${folder}/image${lastCount + 1}`;
      });

      const { data: signatures } = await signAttachmentsService.post('', { timestamp, publicIds: uploadedPublicIds });
      const requests = uploadedAttachment.map(({ item }, idx) => {
        const fd = new FormData();
        const resourceType = item.type.includes('pdf') ? 'raw' : item.type.split('/')[0];
        fd.append('api_key', apiKey);
        fd.append('cloud_name', cloudName);
        fd.append('timestamp', timestamp);
        fd.append('signature', signatures[idx].signature);
        fd.append('public_id', uploadedPublicIds[idx]);
        fd.append('file', item);

        return attachmentsServiceV2.post(`${cloudName}/${resourceType}/upload`, fd);
      });

      const response = await Promise.all(requests);

      // for compatability with existing S3 upload
      const mappedData = response.map(({ data }, idx) => ({
        id: uploadedAttachment[idx].id,
        item: {
          isAttachment: true,
          actualImageUrl: data.secure_url,
          resourceType: data.resource_type,
          fileFormat: data.resource_type === 'raw' ? 'pdf' : data.format,
          description: data.original_filename,
          fileSize: data.bytes,
          isPDFSizeLimitExceeded: convertBytesToMB(data.bytes) > MMS_PDF_MB_LIMIT,
        },
      }));

      dispatch({
        type: actionTypes.GET_ATTACHMENTS_DATASOURCE_SUCCESS,
        payload: [...uploadedSharedLook, ...mappedData],
      });
    } catch (error) {
      dispatch({ type: actionTypes.GET_ATTACHMENTS_DATASOURCE_ERROR, payload: error });
    }
  };

export const uploadToCloudinary =
  (cloudinaryConfig, attachments = [], profilePics) =>
  async (dispatch, getState) => {
    //* we have to resetAdGroup so it does not get requests header
    resetActiveUserADGroup();

    const { cloudName, apiKey } = cloudinaryConfig;

    const checkedAttachments = attachments.map((item) => ({ id: uuidv4(), item }));

    try {
      const timestamp = getSecondsTimestamp();
      const formattedDate = format(new Date(), 'yyyy/M/d');
      // UUID for every group uploads
      const id = uuidv4();
      const { pin } = getState().user.data;

      const uploadedPublicIds = checkedAttachments.map(() => {
        return `OSP/associates/${formattedDate}/${id}/${pin}_${profilePics}`;
      });

      const { data: signatures } = await signAttachmentsService.post('', { timestamp, publicIds: uploadedPublicIds });
      const requests = checkedAttachments.map(({ item }, idx) => {
        const fd = new FormData();
        const resourceType = item.type.includes('pdf') ? 'raw' : item.type.split('/')[0];
        fd.append('api_key', apiKey);
        fd.append('cloud_name', cloudName);
        fd.append('timestamp', timestamp);
        fd.append('signature', signatures[idx].signature);
        fd.append('public_id', uploadedPublicIds[idx]);
        fd.append('file', item);

        return attachmentsServiceV2.post(`${cloudName}/${resourceType}/upload`, fd);
      });

      const response = await Promise.all(requests);

      return response[0].data;
    } catch (error) {
      //eslint-disable-next-line no-console
      console.error(error);
    }
  };

export const sendAttachedFile = (file, generatedUploadData) => async (dispatch) => {
  dispatch({ type: actionTypes.SEND_ATTACHMENTS_LOADING });
  const { signedImageUrl, type } = generatedUploadData;
  try {
    await request(signedImageUrl, {
      method: 'put',
      body: file,
      headers: {
        'Content-Type': type,
        'x-amz-server-side-encryption': 'AES256',
      },
    });
    dispatch({ type: actionTypes.SEND_ATTACHMENTS_SUCCESS });
    return {
      resolution: 'success',
      data: { signedImageUrl, type },
    };
  } catch (error) {
    dispatch({ type: actionTypes.SEND_ATTACHMENTS_ERROR });
    return {
      resolution: 'error',
      data: {
        ...generatedUploadData,
        error,
      },
    };
  }
};

export const removeAttachments = (name) => ({
  type: actionTypes.REMOVE_ATTACHMENTS,
  payload: name,
});

export const getSharedProductDetails =
  (productId, farfetchCheck = false) =>
  async (dispatch) => {
    dispatch({
      type: actionTypes.SHARED_PRODUCT_DETAILS_LOADING,
    });

    try {
      let res = null;
      if (farfetchCheck) {
        res = await farfetchProductDetailsService.get(`/palProductDetails/${productId}`);
      } else {
        res = await productDetailsService.get(`v3/productdetails/${productId}`);
      }
      dispatch({
        type: actionTypes.SHARED_PRODUCT_DETAILS_SUCCESS,
        payload: res.data,
      });
    } catch (error) {
      dispatch({
        type: actionTypes.SHARED_PRODUCT_DETAILS_ERROR,
        payload: { id: productId, error },
      });
    }
  };

export const getEmailHTML = (templateId, payload) => async (dispatch) => {
  dispatch({
    type: actionTypes.EMAIL_TEMPLATE_LOADING,
  });

  const config = {};

  if (payload.brand) {
    config.params = { brand: payload.brand };
  }

  let res;
  try {
    res = await emailTemplatesService.post(templateId, { payload }, config);

    dispatch({
      type: actionTypes.EMAIL_TEMPLATE_SUCCESS,
      payload: res.data.layout,
    });
  } catch (error) {
    dispatch({
      type: actionTypes.EMAIL_TEMPLATE_ERROR,
      payload: error,
    });
  }

  return res?.data?.layout;
};

export const clearEmailHTML = () => ({
  type: actionTypes.CLEAR_EMAIL_TEMPLATE,
});

export const addStyleModuleHistoryIndex = (styleIndex) => ({
  type: actionTypes.ADD_STYLE_MODULE_HISTORY_INDEX,
  payload: styleIndex,
});

export const getSharedContent =
  ({ contactId, associateId, pagination = {}, attachments = 'ALL' }) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.GET_SHARED_CONTENTS_LOADING });

    try {
      const { data } = await conversationServiceV2.post('/conversation', {
        associateId,
        contactId,
        pagination,
        attachments,
      });

      const payload = {
        items: data?.Items,
        count: data?.Count,
      };
      dispatch({ type: actionTypes.GET_SHARED_CONTENTS_SUCCESS, payload });
    } catch (error) {
      dispatch({ type: actionTypes.GET_SHARED_CONTENTS_ERROR, payload: error.response?.data?.message });
    }
  };

export const clearSharedContents = () => ({
  type: actionTypes.CLEAR_SHARED_CONTENTS,
});
